// semeia-disable-reexport
import type Store from "@ember-data/store";
import type {
  CPSInformations,
  DirectAuthenticationStatus,
  DmpDocumentContent,
  FindDocumentsCriteria,
  INS,
  PatientVitaleInformation,
  WebPSURLs
} from "parog-web/cnam/types";
import type { AuthenticationContext } from "parog-web/contexts/authentication-context";
import type DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import type DmpRetrievalStartedJournalEvent from "parog-web/models/dmp-retrieval-started-journal-event";
import {
  numberTriesForLastHourPerCpsId,
  numberTriesForLastTwentyFourHoursPerCpsId
} from "parog-web/utils/dmp-utils";

import type MedicModel from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";

export abstract class DmpAdapter {
  LIMIT_PER_HOUR = 30;
  UPPER_LIMIT_PER_HOUR = 80;
  LIMIT_PER_DAY = 200;

  declare store: Store;
  declare authenticationContext: AuthenticationContext;

  declare currentLimitPerHour: number;

  constructor(public medic: MedicModel) {
    this.currentLimitPerHour = medic?.canUseDmpRetrievalUpperLimit
      ? this.UPPER_LIMIT_PER_HOUR
      : this.LIMIT_PER_HOUR;
  }

  abstract td02Exist(
    ins: INS,
    secretConnection: boolean
  ): Promise<DirectAuthenticationStatus>;
  abstract td03AddAuthorization(
    ins: INS,
    secretConnection: boolean
  ): Promise<void>;
  abstract td31Find(
    ins: INS,
    criteria: FindDocumentsCriteria,
    secretConnection: boolean
  ): Promise<Array<DmpDocumentMetadata>>;

  abstract td32RetrieveContent(
    ins: INS,
    dmpDocumentMetadata: DmpDocumentMetadata,
    secretConnection: boolean
  ): Promise<DmpDocumentContent>;

  abstract td09GetWebPSURLs(
    ins: INS,
    secretConnection: boolean
  ): Promise<WebPSURLs>;

  abstract openSession(): Promise<void>;
  abstract closeSession(): Promise<void>;
  abstract createConnector(secretConnection?: boolean): Promise<void>;

  abstract shouldSetSecretConnection(patient: Patient): Promise<boolean>;

  abstract readCpsCard(): Promise<CPSInformations>;
  abstract readVitaleCard(): Promise<Array<PatientVitaleInformation>>;

  abstract checkAvailability(): Promise<void>;

  // abstract observeCard: TaskForAsyncTaskFunction<
  //   unknown,
  //   (onCardNotAvailable: () => void) => Promise<void>
  // >;

  get currentHourlyRate(): number {
    const jes = this.store.peekAll(
      "dmp-retrieval-started-journal-event"
    ) as unknown as DmpRetrievalStartedJournalEvent[];
    return this.authenticationContext?.cpsInformations
      ? numberTriesForLastHourPerCpsId(
          jes,
          this.authenticationContext.cpsInformations?.medicInternalId
        )
      : 0;
  }

  get currentDailyRate(): number {
    const jes = this.store.peekAll(
      "dmp-retrieval-started-journal-event"
    ) as unknown as DmpRetrievalStartedJournalEvent[];
    return this.authenticationContext?.cpsInformations
      ? numberTriesForLastTwentyFourHoursPerCpsId(
          jes,
          this.authenticationContext.cpsInformations?.medicInternalId
        )
      : 0;
  }

  get hourlyRateExceeded(): boolean {
    return this.currentHourlyRate >= this.currentLimitPerHour;
  }

  get dailyRateExceeded(): boolean {
    return this.currentDailyRate >= this.LIMIT_PER_DAY;
  }

  get hourlyRemainingRate(): number {
    return this.currentLimitPerHour - this.currentHourlyRate;
  }

  get dailyRemainingRate(): number {
    return this.LIMIT_PER_DAY - this.currentDailyRate;
  }

  get remainingRate(): number {
    return Math.min(this.hourlyRemainingRate, this.dailyRemainingRate);
  }
}
